import { Injectable } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, of } from 'rxjs';
import { debounceTime, finalize, first, map, tap } from 'rxjs/operators';
import Fuse from 'fuse.js';

import { OPEN_INSURANCE_ROUTES } from '@app/open-insurance/shared/constants/routes';
import { PlanDataEnum } from '@app/plan/plan.model';
import { PlanService } from '@app/plan/plan.service';
import { PlanManagerService } from '@app/plan-manager/plan-manager.service';

import {
  ISearchItem,
  ISearchMethods,
  SEARCH_ELIGIBILITY_CALLS,
  SEARCH_ITEM_ICON_URL,
  SEARCH_ITEM_TITLE,
  SEARCH_SELECTION_CALLS,
  SearchEligibilityCheckingMethod,
  SearchItemEnum,
  searchItemKeywords,
  SearchPlanManagerItemEnum,
  SearchSelectionMethod
} from './search.model';

@Injectable({
  providedIn: 'root'
})
export class SearchService implements ISearchMethods {
  isDesktopSearchContainerOpened = new BehaviorSubject(false);

  registration: string;
  searchItems = new BehaviorSubject<ISearchItem[]>([]);

  searchForm = this.fb.group({
    searchTerm: this.fb.control('')
  });
  isInvalidSearchTermLength = false;
  isLoadingResults = false;

  constructor(
    private planManagerService: PlanManagerService,
    private planService: PlanService,
    private fb: FormBuilder,
    private router: Router
  ) {
    this.searchForm
      .get('searchTerm')
      .valueChanges.pipe(
        tap((value) => {
          this.isInvalidSearchTermLength = !value || value.length < 3;
          this.isLoadingResults = true;
        }),
        debounceTime(1000)
      )
      .subscribe((value) => {
        if (!this.isInvalidSearchTermLength) {
          this.handleSearch(value);
        }
      });
  }

  get searchResultItems$() {
    return this.searchItems
      .asObservable()
      .pipe(map((items) => items.filter((item) => item.isInSearchResultsList)));
  }

  init(registration: string) {
    this.setSearchItems();
    this.registration = registration;
    this.searchForm.reset(undefined, { emitEvent: false });
    this.isInvalidSearchTermLength = false;
    this.isLoadingResults = false;

    this.setInitialList();
  }

  setSearchItems() {
    const planManagerIconBaseUrl = 'assets/svg/plan-manager';

    const commonInitialData = {
      isInSearchResultsList: false,
      isLoading: false,
      isEligible: undefined
    } as ISearchItem;

    const planManagerItems = Object.values(SearchPlanManagerItemEnum).map(
      (planManagerItem) => {
        return {
          isPlanManagerItem: true,
          id: planManagerItem,
          name: SEARCH_ITEM_TITLE[planManagerItem],
          iconUrl: `${planManagerIconBaseUrl}/${planManagerItem}.svg`,
          keywords: searchItemKeywords[planManagerItem] || [],
          ...commonInitialData
        } as ISearchItem;
      }
    );

    const initialItems = Object.values(SearchItemEnum).map((searchItem) => {
      return {
        isPlanManagerItem: false,
        id: searchItem,
        name: SEARCH_ITEM_TITLE[searchItem],
        iconUrl: SEARCH_ITEM_ICON_URL[searchItem],
        keywords: searchItemKeywords[searchItem] || [],
        ...commonInitialData
      } as ISearchItem;
    });

    this.searchItems.next([...planManagerItems, ...initialItems]);
  }

  setInitialList() {
    const initialItems: (SearchItemEnum | SearchPlanManagerItemEnum)[] = [
      SearchPlanManagerItemEnum.INCOME_TAX,
      SearchPlanManagerItemEnum.NEW_DIVERSIFICATION,
      SearchPlanManagerItemEnum.BOLETOS,
      SearchPlanManagerItemEnum.EXTRA_CONTRIBUTION,
      SearchPlanManagerItemEnum.DEPARTURE_DATE
    ];

    this.setSearchResults(initialItems);
  }

  handleSearch(searchTerm: string) {
    const fuseOptions = {
      keys: ['name', 'keywords'],
      threshold: 0.4,
      minMatchCharLength: 3
    };
    const searchItems = this.searchItems.getValue();
    const fuse = new Fuse(searchItems, fuseOptions);
    const result = fuse.search(searchTerm);

    const matchedItemsIds = result.map((resultItem) => resultItem.item.id);
    this.setSearchResults(matchedItemsIds);
    this.isLoadingResults = false;
  }

  setSearchResults(items: (SearchItemEnum | SearchPlanManagerItemEnum)[]) {
    const updatedResults = this.searchItems.value.map((searchItem) => {
      const isInSearchResultsList = items.some(
        (item) => item === searchItem.id
      );
      const isNotEligibilityChecked = searchItem.isEligible === undefined;
      const isLoading = isInSearchResultsList && isNotEligibilityChecked;

      return {
        ...searchItem,
        isInSearchResultsList,
        isLoading
      } as ISearchItem;
    });

    this.searchItems.next(updatedResults);
    updatedResults
      .filter((searchItem) => {
        return searchItem.isLoading;
      })
      .forEach((searchItem) => {
        if (searchItem.isPlanManagerItem) {
          this.checkPlanManagerItemEligibility(searchItem);
        } else {
          this.checkItemEligibility(searchItem);
        }
      });
  }

  checkPlanManagerItemEligibility(searchItem: ISearchItem) {
    const isPlanManagerItem = true;
    const eligibilityCall = this.planManagerService.getEligibilityCall(
      searchItem.id as SearchPlanManagerItemEnum
    );

    if (eligibilityCall) {
      let isEligible = false;
      let data = undefined;

      eligibilityCall(this.registration)
        .pipe(
          tap((response: unknown | undefined) => {
            isEligible = Boolean(response);
            data = response;
          }),
          finalize(() => {
            this.setItemEligibility(
              searchItem.id,
              isPlanManagerItem,
              isEligible,
              data
            );
          })
        )
        .subscribe();
    } else {
      const isEligible = true;
      const data = this.planManagerService.getBasicFeatData(this.registration);

      this.setItemEligibility(
        searchItem.id,
        isPlanManagerItem,
        isEligible,
        data
      );
    }
  }

  checkItemEligibility(searchItem: ISearchItem) {
    const isPlanManagerItem = false;
    const eligibilityCallName = SEARCH_ELIGIBILITY_CALLS[searchItem.id];

    if (eligibilityCallName) {
      let isEligible = false;
      const eligibilityCall: SearchEligibilityCheckingMethod = (
        this[eligibilityCallName] as SearchEligibilityCheckingMethod
      ).bind(this);

      eligibilityCall()
        .pipe(
          tap((response: unknown | undefined) => {
            isEligible = Boolean(response);
          }),
          finalize(() => {
            this.setItemEligibility(
              searchItem.id,
              isPlanManagerItem,
              isEligible
            );
          })
        )
        .subscribe();
    } else {
      const isEligible = true;

      this.setItemEligibility(searchItem.id, isPlanManagerItem, isEligible);
    }
  }

  setItemEligibility(
    searchItemId: SearchItemEnum | SearchPlanManagerItemEnum,
    isPlanManagerItem: boolean,
    isEligible: boolean,
    planManagerItemData?: unknown
  ) {
    const items = this.searchItems.getValue().map((item) => {
      if (item.id === searchItemId) {
        const updatedItem = {
          ...item,
          isLoading: false,
          isEligible
        } satisfies ISearchItem;

        if (!isEligible) {
          return updatedItem;
        }

        updatedItem.onSelection = isPlanManagerItem
          ? this.getPlanManagerSelectionCallback(
              searchItemId as SearchPlanManagerItemEnum,
              planManagerItemData
            )
          : this.getSelectionCallback(searchItemId as SearchItemEnum);

        return updatedItem;
      }
      return item;
    });

    this.searchItems.next(items);
  }

  getPlanManagerSelectionCallback(
    planManagerFeatId: SearchPlanManagerItemEnum,
    data: unknown
  ) {
    const selectionCall =
      this.planManagerService.getSelectionCall(planManagerFeatId);

    return () => {
      selectionCall(data).subscribe();
    };
  }

  getSelectionCallback(searchItemId: SearchItemEnum) {
    const selectionCallName = SEARCH_SELECTION_CALLS[searchItemId];
    const selectionCall = (
      this[selectionCallName] as SearchSelectionMethod
    ).bind(this);

    return () => selectionCall().subscribe();
  }

  handleClick(searchResult: ISearchItem) {
    searchResult.onSelection();
    this.isDesktopSearchContainerOpened.next(false);
  }

  navigateOnItemSelection(url: string) {
    return of(null).pipe(
      tap(() => {
        this.router.navigateByUrl(url);
      })
    );
  }

  isListPlansEligible() {
    const isSinglePlan = Boolean(
      this.planService.getPlanData(PlanDataEnum.SinglePlan)
    );
    const isEligible = !isSinglePlan;

    return of(isEligible).pipe(first());
  }

  onSupportSelection() {
    return this.navigateOnItemSelection('/support');
  }
  onListPlansSelection() {
    return this.navigateOnItemSelection('/');
  }
  onProfileSelection() {
    return this.navigateOnItemSelection('/profile');
  }
  onOpenInsuranceSelection() {
    const url = OPEN_INSURANCE_ROUTES.HOME;
    return this.navigateOnItemSelection(url);
  }
  onPrivacySelection() {
    return this.navigateOnItemSelection('/privacy');
  }
}
